import { add, cross, distance, dot, normalize, scale, subtract, type Quat, type Vec3 } from "./math";

export const TOTAL_LAPS = 3;
export const TRACK_WIDTH = 17;
export const TRACK_SAMPLE_COUNT = 144;
export const CHECKPOINT_TS = [0, 0.105, 0.205, 0.305, 0.405, 0.53, 0.64, 0.755, 0.875] as const;

export const TRACK_CONTROL_POINTS: readonly Vec3[] = [
  [0, 3, 72],
  [-48, 4, 70],
  [-86, 7, 40],
  [-97, 9, -8],
  [-75, 14, -52],
  [-32, 23, -76],
  [13, 13, -83],
  [56, 8, -62],
  [88, 7, -26],
  [94, 11, 20],
  [72, 18, 56],
  [36, 5, 73],
] as const;

function wrapIndex(index: number): number {
  const count = TRACK_CONTROL_POINTS.length;
  return ((index % count) + count) % count;
}

function catmullComponent(p0: number, p1: number, p2: number, p3: number, t: number): number {
  const t2 = t * t;
  const t3 = t2 * t;
  return 0.5 * ((2 * p1) + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 + (-p0 + 3 * p1 - 3 * p2 + p3) * t3);
}

export function sampleTrack(progress: number): Vec3 {
  const wrapped = ((progress % 1) + 1) % 1;
  const scaled = wrapped * TRACK_CONTROL_POINTS.length;
  const segment = Math.floor(scaled);
  const local = scaled - segment;
  const p0 = TRACK_CONTROL_POINTS[wrapIndex(segment - 1)]!;
  const p1 = TRACK_CONTROL_POINTS[wrapIndex(segment)]!;
  const p2 = TRACK_CONTROL_POINTS[wrapIndex(segment + 1)]!;
  const p3 = TRACK_CONTROL_POINTS[wrapIndex(segment + 2)]!;
  return [
    catmullComponent(p0[0], p1[0], p2[0], p3[0], local),
    catmullComponent(p0[1], p1[1], p2[1], p3[1], local),
    catmullComponent(p0[2], p1[2], p2[2], p3[2], local),
  ];
}

export function trackTangent(progress: number): Vec3 {
  return normalize(subtract(sampleTrack(progress + 0.001), sampleTrack(progress - 0.001)));
}

export function trackBank(progress: number): number {
  const wrapped = ((progress % 1) + 1) % 1;
  if (wrapped >= 0.72 && wrapped <= 0.84) {
    const local = (wrapped - 0.72) / 0.12;
    return Math.sin(Math.PI * local) * 1.28;
  }
  if (wrapped >= 0.16 && wrapped <= 0.27) {
    return -Math.sin(Math.PI * ((wrapped - 0.16) / 0.11)) * 0.38;
  }
  return 0;
}

export function trackFrame(progress: number): { position: Vec3; tangent: Vec3; right: Vec3; up: Vec3; bank: number } {
  const tangent = trackTangent(progress);
  const worldUp: Vec3 = [0, 1, 0];
  const flatRight = normalize(cross(worldUp, tangent));
  const baseUp = normalize(cross(tangent, flatRight));
  const bank = trackBank(progress);
  const cosine = Math.cos(bank);
  const sine = Math.sin(bank);
  const right = normalize(add(scale(flatRight, cosine), scale(baseUp, sine)));
  const up = normalize(add(scale(baseUp, cosine), scale(flatRight, -sine)));
  return { position: sampleTrack(progress), tangent, right, up, bank };
}

export const CHECKPOINTS = CHECKPOINT_TS.map((progress, index) => ({
  index,
  progress,
  position: sampleTrack(progress),
  radius: index === 0 ? 15 : 17,
}));

export function segmentCrossesCheckpoint(from: Vec3, to: Vec3, checkpointIndex: number): boolean {
  const checkpoint = CHECKPOINTS[checkpointIndex];
  if (!checkpoint) return false;
  const segment = subtract(to, from);
  const lengthSquared = dot(segment, segment);
  const amount = lengthSquared > 0 ? Math.max(0, Math.min(1, dot(subtract(checkpoint.position, from), segment) / lengthSquared)) : 0;
  const closest = add(from, scale(segment, amount));
  return distance(closest, checkpoint.position) <= checkpoint.radius;
}

function yawQuaternion(direction: Vec3): Quat {
  const yaw = Math.atan2(-direction[0], -direction[2]);
  return [0, Math.sin(yaw / 2), 0, Math.cos(yaw / 2)];
}

export function spawnTransform(lane: number, checkpointIndex = 0): { position: Vec3; rotation: Quat } {
  const checkpoint = CHECKPOINTS[checkpointIndex] ?? CHECKPOINTS[0]!;
  const progress = checkpointIndex === 0 ? 0.975 : checkpoint.progress;
  const frame = trackFrame(progress);
  const laneOffset = ((lane % 4) - 1.5) * 3.3;
  const rowOffset = Math.floor(lane / 2) * -4;
  return {
    position: add(add(frame.position, scale(frame.right, laneOffset)), add(scale(frame.up, 2.1), scale(frame.tangent, rowOffset))),
    rotation: yawQuaternion(frame.tangent),
  };
}

export function nearestTrackProgress(position: Vec3, samples = TRACK_SAMPLE_COUNT): number {
  let bestProgress = 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (let index = 0; index < samples; index += 1) {
    const progress = index / samples;
    const candidateDistance = distance(position, sampleTrack(progress));
    if (candidateDistance < bestDistance) {
      bestDistance = candidateDistance;
      bestProgress = progress;
    }
  }
  return bestProgress;
}
